import { useState } from "react";

import {
  Box,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
} from "@mui/material";

import { X } from "@phosphor-icons/react";

import { isHtmlContentEmpty } from "@/utils/htmlContentStyle";

import { EditingField } from "../../tutor/Tabs/Problems/features";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (prompt: string) => void;
}

interface ValidationErrors {
  prompt?: string;
}

const CreateExampleModal = ({ isOpen, onClose, onCreate }: Props) => {
  const [prompt, setPrompt] = useState("");
  const [errors, setErrors] = useState<ValidationErrors>({});
  const isPromptEmpty = isHtmlContentEmpty(prompt);

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (isPromptEmpty) {
      newErrors.prompt = "Prompt is required";
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  const handleCreate = () => {
    if (validateForm()) {
      onCreate(prompt);
      handleClose();
    }
  };

  const resetForm = () => {
    setPrompt("");
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handlePromptChange = (value: string) => {
    setPrompt(value);
    if (errors.prompt) {
      setErrors((prev) => ({
        ...prev,
        prompt: undefined,
      }));
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="text14B">Create Example</Typography>
        <IconButton
          aria-label="Close create example dialog"
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: -24,
            right: -24,
            color: "common.white",
          }}
        >
          <X size={18} weight="bold" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="text13R" color="text.secondary">
            Add context to generate a targeted example for this lesson.
          </Typography>
        </Box>
        <EditingField
          label="Prompt"
          value={prompt}
          onChange={handlePromptChange}
          type="textarea"
          labelVariant="text13M"
          placeholder="Provide a prompt to generate an example"
          isEditing={true}
          initialHeight={200}
          error={!!errors.prompt}
          helperText={errors.prompt}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={isPromptEmpty}
          sx={{
            borderRadius: 1.5,
            px: 3,
            backgroundColor: "secondary.main",
            "&:hover": {
              backgroundColor: "brand.green",
            },
          }}
        >
          Create
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleClose}
          sx={{
            borderRadius: 1.5,
            px: 3,
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateExampleModal;
